/**
 * Metric accent + typical sport relevance — visual layer only.
 * Does not compute or replace activity analytics.
 */

import {
  Activity,
  Flame,
  Heart,
  Mountain,
  Route,
  Timer,
  Zap,
  Gauge,
} from "lucide-react";
import { METRIC_COLORS } from "./designTokens";

export const METRIC_VISUALS = {
  brand: { icon: Activity, accent: METRIC_COLORS.brand, label: "Brand", soft: "rgba(49, 185, 120, 0.14)" },
  distance: { icon: Route, accent: METRIC_COLORS.distance, label: "Distance", soft: "rgba(47, 142, 234, 0.14)" },
  speed: { icon: Gauge, accent: METRIC_COLORS.speed, label: "Speed", soft: "rgba(47, 142, 234, 0.14)" },
  pace: { icon: Timer, accent: METRIC_COLORS.pace, label: "Pace", soft: "rgba(47, 142, 234, 0.14)" },
  duration: { icon: Timer, accent: METRIC_COLORS.duration, label: "Duration", soft: "rgba(130, 105, 200, 0.14)" },
  calories: { icon: Flame, accent: METRIC_COLORS.calories, label: "Calories", soft: "rgba(217, 154, 43, 0.14)" },
  hr: { icon: Heart, accent: METRIC_COLORS.hr, label: "Heart rate", soft: "rgba(216, 91, 91, 0.14)" },
  elevation: { icon: Mountain, accent: METRIC_COLORS.elevation, label: "Elevation", soft: "rgba(49, 185, 120, 0.14)" },
  power: { icon: Zap, accent: METRIC_COLORS.power, label: "Power", soft: "rgba(217, 154, 43, 0.14)" },
  cadence: { icon: Activity, accent: METRIC_COLORS.cadence, label: "Cadence", soft: "rgba(42, 168, 160, 0.16)" },
  ftp: { icon: Zap, accent: METRIC_COLORS.power, label: "FTP", soft: "rgba(217, 154, 43, 0.14)" },
  wkg: { icon: Zap, accent: METRIC_COLORS.power, label: "W/kg", soft: "rgba(217, 154, 43, 0.14)" },
  laps: { icon: Activity, accent: METRIC_COLORS.neutral, label: "Laps", soft: "rgba(127, 138, 147, 0.16)" },
  neutral: { icon: Activity, accent: METRIC_COLORS.neutral, label: "General", soft: "rgba(127, 138, 147, 0.16)" },
};

const TIMELINE_METRIC_KEYS = {
  distance: "distance",
  time: "duration",
  elevation: "elevation",
};

export function visualForTimelineMetric(timelineKey) {
  return getMetricVisual(TIMELINE_METRIC_KEYS[timelineKey] || timelineKey);
}

/** Typical metrics by sport for later page layouts. Presence of real data still wins. */
export const SPORT_METRIC_KEYS = {
  cycling: ["power", "ftp", "wkg", "cadence", "speed", "elevation", "hr", "distance", "duration", "calories"],
  running: ["pace", "hr", "cadence", "elevation", "distance", "duration", "calories", "power"],
  swimming: ["pace", "distance", "duration", "laps", "hr", "calories"],
  walking: ["pace", "speed", "distance", "elevation", "hr", "duration", "calories"],
  hiking: ["pace", "speed", "distance", "elevation", "hr", "duration", "calories"],
  other: ["distance", "duration", "hr", "elevation", "calories"],
};

/** Preferred activity-card metric order. Distance and duration stay first; only populated fields are shown. */
export const SPORT_CARD_METRIC_KEYS = {
  cycling: ["distance", "duration", "speed", "hr", "power", "cadence", "calories", "elevation"],
  running: ["distance", "duration", "pace", "hr", "cadence", "elevation", "calories", "power"],
  swimming: ["distance", "duration", "pace", "laps", "hr", "calories"],
  walking: ["distance", "duration", "pace", "hr", "elevation", "calories", "speed"],
  hiking: ["distance", "duration", "speed", "hr", "elevation", "calories"],
  other: ["distance", "duration", "hr", "elevation", "calories"],
};

export function getMetricVisual(metricKey) {
  return METRIC_VISUALS[metricKey] || METRIC_VISUALS.neutral;
}

export function hasMetricValue(value) {
  if (value == null || value === "" || value === "—") return false;
  if (typeof value === "number" && !Number.isFinite(value)) return false;
  return true;
}

/** Show a metric when the activity actually has a value. Sport lists are layout hints only. */
export function shouldDisplayMetric(value) {
  return hasMetricValue(value);
}

export function typicalMetricsForSport(sportKey) {
  return SPORT_METRIC_KEYS[sportKey] || SPORT_METRIC_KEYS.other;
}

export function cardMetricsForSport(sportKey) {
  return SPORT_CARD_METRIC_KEYS[sportKey] || SPORT_CARD_METRIC_KEYS.other;
}
